import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, FileSpreadsheet, Check, AlertTriangle, FileUp, Import, Bot, Download, Loader2 } from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import SingleSelectApiDropdown from '../../components/SingleSelectApiDropdown';
import { useClientLevel } from '../../hooks/useClientLevel';

interface UploadCamerasModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const UploadModal: React.FC<UploadCamerasModalProps> = ({
    open,
    onClose,
    onSuccess
}) => {
    const [agent, setAgent] = useState<any | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloadingTemplate, setDownloadingTemplate] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const level = useClientLevel();

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (!open) return;
        setAgent(null);
        setFile(null);
        setErrors({});
        setDownloadingTemplate(false);
    }, [open]);

    const handleClose = () => {
        setAgent(null);
        setFile(null);
        setErrors({});
        onClose();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const selectedFile = e.target.files[0];

            // Optional: Basic validation for excel/csv extensions
            const validExtensions = ['.xlsx', '.xls', '.csv'];
            const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();

            if (!validExtensions.includes(fileExtension)) {
                setErrors(prev => ({ ...prev, file: 'Please upload a valid Excel or CSV file (.xlsx, .xls, .csv)' }));
                setFile(null);
                return;
            }

            setFile(selectedFile);
            setErrors(prev => {
                const next = { ...prev };
                delete next.file;
                return next;
            });
        }
    };

    const validateForm = (): boolean => {
        const nextErrors: Record<string, string> = {};

        if (!agent) nextErrors.agent = 'Please select an agent';
        if (!file) nextErrors.file = 'Please select a file to upload';

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleDownloadTemplate = async () => {
        try {
            setDownloadingTemplate(true);

            // responseType: 'blob' is required to handle the file download properly
            const response = await baseapi.get('/agent/assets/template', {
                responseType: 'blob'
            });

            // Create a blob from the response
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);

            // Create a temporary link element to trigger download
            const link = document.createElement('a');
            link.href = url;

            // Try to extract the file name from headers, fallback to default
            const contentDisposition = response.headers['content-disposition'];
            let fileName = 'asset_template.xlsx';
            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (fileNameMatch && fileNameMatch.length === 2) {
                    fileName = fileNameMatch[1];
                }
            }

            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();

            // Clean up the URL object
            window.URL.revokeObjectURL(url);
            showSuccess('Template downloaded successfully');
        } catch (error: any) {
            console.error('Failed to download template', error);
            showError('Failed to download template file');
        } finally {
            setDownloadingTemplate(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            // showError('Please fix validation errors');
            return;
        }

        try {
            setLoading(true);

            // Using FormData for multipart/form-data
            const formData = new FormData();
            formData.append('file', file as Blob);
            formData.append('agent_id', String(agent.id));
            formData.append('client_id', String(level?.id));

            const response = await baseapi.post('/agent/assets/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.status) {
                showSuccess('Cameras uploaded successfully');
                onSuccess();
                handleClose();
            } else {
                showError(response.data.message || 'Failed to upload cameras');
            }
        } catch (error: any) {
            showError(error.response?.data?.message || error.message || 'Upload failed');
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]" onClick={handleClose} />

            <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4">
                <div className="w-full max-w-lg bg-white dark:bg-gray-900/95 backdrop-blur-xl border border-gray-200 dark:border-orange-400/20 shadow-2xl rounded-xl text-gray-900 dark:text-white relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />

                    <div className="sticky top-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-b border-gray-200 dark:border-gray-800 px-6 py-4 z-10">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <Import className="h-5 w-5 text-orange-400 mr-2" />
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                    Import Assets
                                </h2>
                            </div>

                            {/* Download Template and Close buttons */}
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handleDownloadTemplate}
                                    disabled={downloadingTemplate}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 border border-gray-200 dark:bg-gray-800 dark:border-gray-700 dark:hover:border-orange-500 dark:hover:bg-gray-700 text-orange-600 dark:text-orange-400 rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {downloadingTemplate ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                        <Download className="w-3.5 h-3.5" />
                                    )}
                                    Template
                                </button>

                                <button onClick={handleClose} className="text-gray-400 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white transition-colors p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="p-6">
                        <div className="space-y-6">

                            {/* Agent Selection Dropdown */}
                            <div>
                                <SingleSelectApiDropdown
                                    label="Select Agent"
                                    value={agent}
                                    onChange={(item) => {
                                        setAgent(item);
                                        if (item) setErrors(prev => ({ ...prev, agent: '' }));
                                    }}
                                    apiEndpoint="/agents"
                                    icon={Bot}
                                    placeholder="Search and select agent..."
                                    searchPlaceholder="Search agents..."
                                    searchInputPlaceholder="Type agent name..."
                                    displayProperty="name" // Change this if your API returns the name under a different key
                                    searchFields={['name']}
                                    required={true}
                                    additionalQueries={{
                                        client_id: level.id,
                                        agent_type: 'probe'
                                    }}

                                />
                                {errors.agent && (
                                    <p className="mt-1.5 text-xs text-red-400 flex items-center">
                                        <AlertTriangle className="w-3 h-3 mr-1" /> {errors.agent}
                                    </p>
                                )}
                            </div>

                            {/* File Upload Area */}
                            <div>
                                <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                    <FileSpreadsheet className="w-3.5 h-3.5 text-orange-400 mr-2" />
                                    Excel/CSV File <span className="text-red-400 ml-1">*</span>
                                </label>

                                <div
                                    onClick={() => fileInputRef.current?.click()}
                                    className={`relative cursor-pointer flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl transition-all duration-300 bg-gray-50 dark:bg-gray-800/30
                                        ${file ? 'border-orange-500/50 bg-orange-500/5' : errors.file ? 'border-red-500/50 hover:bg-gray-100 dark:hover:bg-gray-800' : 'border-gray-300 dark:border-gray-600 hover:border-orange-500/50 hover:bg-gray-100 dark:hover:bg-gray-800'}
                                    `}
                                >
                                    <input
                                        type="file"
                                        className="hidden"
                                        ref={fileInputRef}
                                        accept=".xlsx, .xls, .csv"
                                        onChange={handleFileChange}
                                    />

                                    {file ? (
                                        <div className="flex flex-col items-center text-center px-4">
                                            <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center mb-2">
                                                <FileSpreadsheet className="w-5 h-5 text-orange-400" />
                                            </div>
                                            <span className="text-sm font-medium text-orange-600 dark:text-orange-300 truncate max-w-[250px]">{file.name}</span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">{(file.size / 1024).toFixed(1)} KB</span>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center text-center px-4">
                                            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mb-2">
                                                <FileUp className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                                            </div>
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Click to browse files</span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Supports .xlsx, .xls, .csv</span>
                                        </div>
                                    )}
                                </div>
                                {errors.file && (
                                    <p className="mt-1.5 text-xs text-red-400 flex items-center">
                                        <AlertTriangle className="w-3 h-3 mr-1" /> {errors.file}
                                    </p>
                                )}
                            </div>

                        </div>

                        {/* Footer Buttons */}
                        <div className="flex justify-end gap-3 mt-8 pt-5 border-t border-gray-200 dark:border-gray-800">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={loading || downloadingTemplate}
                                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500 transition-colors disabled:opacity-50"
                            >
                                <X className="w-4 h-4" />
                                Cancel
                            </button>

                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={loading || downloadingTemplate}
                                className="flex items-center justify-center gap-1.5 px-5 py-2 text-sm bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg font-semibold hover:from-orange-700 hover:to-orange-800 transition-all shadow-lg shadow-orange-900/20 disabled:opacity-50 min-w-[130px]"
                            >
                                {loading ? (
                                    <>
                                        <Upload className="w-4 h-4 animate-bounce" />
                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <Check className="w-4 h-4" />
                                        Upload File
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default UploadModal;
